import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Clock, CheckCircle, XCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface Task {
  id: string;
  task_type: string;
  status: string;
  progress: number;
  message: string;
  created_at: string;
  started_at?: string;
  completed_at?: string;
}

export const ActiveTasksCard: React.FC = () => {
  const { data: tasks = [], isLoading, refetch } = useQuery<Task[]>({
    queryKey: ['active-tasks'],
    queryFn: async () => {
      const response = await apiRequest('GET', '/api/players/tasks/active');
      if (!response.ok) {
        throw new Error('Failed to fetch active tasks');
      }
      return await response.json();
    },
    refetchInterval: 5000, // Refetch every 5 seconds for active tasks
    enabled: true
  });

  const activeTasks = tasks.filter(t => t.status !== 'completed' && t.status !== 'failed' && t.status !== 'cancelled');

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center h-32">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (activeTasks.length === 0) {
    return null; // Don't show card if no active tasks
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4" />;
      case 'started':
      case 'progress':
        return <Loader2 className="h-4 w-4 animate-spin" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-600" />;
      default:
        return <AlertCircle className="h-4 w-4" />;
    }
  };

  const getStatusColor = (status: string): "default" | "secondary" | "destructive" | "outline" => {
    switch (status) {
      case 'completed':
        return 'secondary';
      case 'failed':
        return 'destructive';
      default:
        return 'default';
    }
  };

  const formatTaskType = (type: string) => {
    return type.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const formatDuration = (startedAt?: string) => {
    if (!startedAt) return '';
    const start = new Date(startedAt);
    const now = new Date();
    const diff = Math.floor((now.getTime() - start.getTime()) / 1000); // seconds
    
    if (diff < 60) return `${diff}s`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    return `${Math.floor(diff / 3600)}h ${Math.floor((diff % 3600) / 60)}m`;
  };

  return (
    <Card className="lg:col-span-2">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-lg font-semibold">Active Background Tasks</CardTitle>
        <Button 
          variant="ghost" 
          size="sm"
          onClick={() => refetch()}
        >
          <Loader2 className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {activeTasks.map((task) => (
          <div key={task.id} className="space-y-2 p-4 border rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {getStatusIcon(task.status)}
                <span className="font-medium">{formatTaskType(task.task_type)}</span>
                <Badge variant={getStatusColor(task.status)} className="text-xs">
                  {task.status}
                </Badge>
              </div>
              {task.started_at && (
                <span className="text-sm text-muted-foreground">
                  {formatDuration(task.started_at)}
                </span>
              )}
            </div>
            
            {task.progress > 0 && (
              <Progress value={task.progress} className="h-2" />
            )}
            
            {task.message && (
              <p className="text-sm text-muted-foreground">{task.message}</p>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
};